import { marked } from 'marked';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import { isListLine } from './text-cleaning';
import { sanitizeHtml } from './sanitize';
import type { ChangeStats } from '../types/app';

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  })[character] ?? character);
}

export function markdownToHtml(markdown: string, stats?: ChangeStats): string {
  if (stats) {
    const patterns = markdown.match(/(?:\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_|^#{1,6}\s+|^\s*(?:[-*+] |\d+\. )|\[[^\]]+\]\([^)]+\)|^>\s+|^```)/gm);
    stats.markdownConversions += patterns?.length ?? 0;
  }
  const parsed = marked.parse(markdown, { gfm: true, breaks: false, async: false }) as string;
  return sanitizeHtml(parsed, stats);
}

function paragraphToHtml(lines: string[]): string {
  return `<p>${lines.map((line) => escapeHtml(line)).join('<br>')}</p>`;
}

export function plainTextToHtml(text: string, detectLists: boolean, stats?: ChangeStats): string {
  const lines = text.split('\n');
  const output: string[] = [];
  let paragraph: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) output.push(paragraphToHtml(paragraph));
    paragraph = [];
  };
  const flushList = () => {
    if (listType && listItems.length > 0) {
      output.push(`<${listType}>${listItems.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</${listType}>`);
      if (stats) stats.listsConverted += 1;
    }
    listType = null;
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    if (detectLists && isListLine(trimmed)) {
      flushParagraph();
      const ordered = /^\d+[.)]\s+/.test(trimmed);
      const type = ordered ? 'ol' : 'ul';
      if (listType && listType !== type) flushList();
      listType = type;
      listItems.push(trimmed.replace(/^\s*(?:[-*+•‣▪◦]|\d+[.)])\s+/, ''));
      continue;
    }
    flushList();
    if (/^(?:https?:\/\/|www\.)\S+$/i.test(trimmed)) {
      const href = /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
      output.push(`<p><a href="${escapeHtml(href)}">${escapeHtml(trimmed)}</a></p>`);
    } else {
      paragraph.push(trimmed);
    }
  }
  flushParagraph();
  flushList();
  return sanitizeHtml(output.join(''));
}

export function htmlToMarkdown(html: string): string {
  const service = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
    strongDelimiter: '**',
  });
  service.use(gfm);
  return service.turndown(sanitizeHtml(html)).replace(/\n{3,}/g, '\n\n').trim();
}

function blockText(node: Node, depth = 0): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof HTMLElement)) return '';
  const tag = node.tagName.toLowerCase();
  const children = () => [...node.childNodes].map((child) => blockText(child, depth)).join('');
  if (tag === 'br') return '\n';
  if (tag === 'strong' || tag === 'b') return `*${children().trim()}*`;
  if (tag === 'em' || tag === 'i') return `_${children().trim()}_`;
  if (tag === 's' || tag === 'del') return `~${children().trim()}~`;
  if (tag === 'code' && node.parentElement?.tagName.toLowerCase() !== 'pre') return `\`${children().trim()}\``;
  if (tag === 'a') {
    const label = children().trim();
    const href = node.getAttribute('href') ?? '';
    return href && href !== label ? `${label} (${href})` : label || href;
  }
  if (/^h[1-6]$/.test(tag)) return `\n*${children().trim()}*\n`;
  if (tag === 'p') return `\n${children().trim()}\n`;
  if (tag === 'blockquote') return `\n${children().trim().split('\n').map((line) => `> ${line}`).join('\n')}\n`;
  if (tag === 'pre') return `\n\`\`\`\n${node.textContent?.trim() ?? ''}\n\`\`\`\n`;
  if (tag === 'li') {
    const parent = node.parentElement?.tagName.toLowerCase();
    const index = parent === 'ol' ? [...(node.parentElement?.children ?? [])].indexOf(node) + 1 : 0;
    return `\n${'  '.repeat(depth)}${parent === 'ol' ? `${index}.` : '•'} ${children().trim()}`;
  }
  if (tag === 'ul' || tag === 'ol') return `${[...node.childNodes].map((child) => blockText(child, depth)).join('')}\n`;
  if (tag === 'tr') return `\n${[...node.children].map((cell) => cell.textContent?.trim() ?? '').join(' | ')}`;
  if (tag === 'table') return `\n${children()}\n`;
  return children();
}

export function htmlToWhatsApp(html: string): string {
  const documentNode = new DOMParser().parseFromString(`<body>${sanitizeHtml(html)}</body>`, 'text/html');
  return [...documentNode.body.childNodes]
    .map((node) => blockText(node))
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function plainBlock(node: Node, depth = 0): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof HTMLElement)) return '';
  const tag = node.tagName.toLowerCase();
  const children = () => [...node.childNodes].map((child) => plainBlock(child, depth)).join('');
  if (tag === 'br') return '\n';
  if (/^h[1-6]$/.test(tag) || tag === 'p' || tag === 'blockquote' || tag === 'pre') return `\n${children().trim()}\n`;
  if (tag === 'li') {
    const parent = node.parentElement?.tagName.toLowerCase();
    const index = parent === 'ol' ? [...(node.parentElement?.children ?? [])].indexOf(node) + 1 : 0;
    return `\n${'  '.repeat(depth)}${parent === 'ol' ? `${index}.` : '•'} ${children().trim()}`;
  }
  if (tag === 'ul' || tag === 'ol') return `${[...node.childNodes].map((child) => plainBlock(child, depth)).join('')}\n`;
  if (tag === 'tr') return `\n${[...node.children].map((cell) => cell.textContent?.trim() ?? '').join('\t')}`;
  if (tag === 'a') {
    const label = children().trim();
    const href = node.getAttribute('href') ?? '';
    return href && href !== label ? `${label} (${href})` : label || href;
  }
  return children();
}

export function htmlToPlainText(html: string): string {
  const documentNode = new DOMParser().parseFromString(`<body>${sanitizeHtml(html)}</body>`, 'text/html');
  return [...documentNode.body.childNodes]
    .map((node) => plainBlock(node))
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
